import type { FC, ReactNode } from "react";
import { useLocalization } from "@fluent/react";
import { IconButton, Tooltip, useTheme } from "@mui/material";
import { alpha, darken, lighten } from "@mui/material/styles";
import type { SxProps, Theme } from "@mui/material/styles";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

const APPEARANCES = {
    neutral: {
        color: "#009EAA", background: "#F4FBFC", border: "#C8E4E7",
        hoverBackground: "#E4F4F6", hoverBorder: "#009EAA",
    },
    teal: {
        color: "#FFFFFF", background: "#258A80", border: "#207E75",
        hoverBackground: "#1C756C", hoverBorder: "#17685F",
    },
    graphite: {
        color: "#FFFFFF", background: "#415563", border: "#415563",
        hoverBackground: "#344652", hoverBorder: "#344652",
    },
    info: {
        color: "#0277BD", background: "#EAF4FC", border: "#B5D9F2",
        hoverBackground: "#D9ECFA", hoverBorder: "#0277BD",
    },
} as const;

export interface GoToButtonProps {
    onClick: () => void;
    appearance?: keyof typeof APPEARANCES;
    frameless?: boolean;
    icon?: ReactNode;
    label?: ReactNode;
    ariaLabel?: string;
    disabled?: boolean;
    size?: "small" | "medium";
    iconSize?: number;
    tabIndex?: number;
    sx?: SxProps<Theme>;
}

export const GoToButton: FC<GoToButtonProps> = ({
    onClick,
    appearance = "neutral",
    frameless = true,
    icon = <OpenInNewIcon />,
    label,
    ariaLabel,
    disabled = false,
    size = "small",
    iconSize = 20,
    tabIndex,
    sx = [],
}) => {
    const theme = useTheme();
    const { l10n } = useLocalization();
    const colors = APPEARANCES[appearance];
    const dimension = size === "small" ? 32 : 36;
    const darkSoft = theme.palette.mode === "dark"
        && (appearance === "neutral" || appearance === "info");
    const rightColorValue = theme.palette.text.secondary;

    const color = appearance === "neutral"
        ? rightColorValue
        : (darkSoft ? lighten(colors.color, 0.4) : colors.color);
    const background = darkSoft ? alpha(color, 0.12) : colors.background;
    const border = darkSoft ? alpha(color, 0.4) : colors.border;
    const hoverBackground = darkSoft ? alpha(color, 0.22) : colors.hoverBackground;
    const hoverBorder = darkSoft ? color : colors.hoverBorder;

    const defaultLabel = l10n.getString("go-to", null, "_Przejdź");
    const accessibleLabel = ariaLabel
        ?? (typeof label === "string" ? label : defaultLabel);

    return (
        <Tooltip title={label ?? accessibleLabel} arrow describeChild enterDelay={400}>
            <span style={{ display: "inline-flex", verticalAlign: "middle" }}>
                <IconButton
                    type="button"
                    onClick={onClick}
                    disabled={disabled}
                    aria-label={accessibleLabel}
                    tabIndex={tabIndex}
                    size={size}
                    sx={[
                        {
                            width: dimension,
                            height: dimension,
                            p: 0,
                            flexShrink: 0,
                            boxSizing: "border-box",
                            borderRadius: "6px",
                            border: frameless ? "none" : "1px solid",
                            borderColor: frameless ? "transparent" : border,
                            bgcolor: frameless ? "transparent" : background,
                            color,
                            transition: "background-color 150ms, border-color 150ms",
                            "& .MuiSvgIcon-root": { fontSize: iconSize },
                            "&:hover": {
                                bgcolor: frameless ? alpha(color, 0.1) : hoverBackground,
                                borderColor: frameless ? "transparent" : hoverBorder,
                            },
                            "&:active": {
                                bgcolor: frameless
                                    ? alpha(color, 0.16)
                                    : (darkSoft
                                        ? alpha(color, 0.3)
                                        : darken(hoverBackground, 0.06)),
                            },
                            "&.Mui-focusVisible": {
                                outline: `2px solid ${hoverBorder}`,
                                outlineOffset: "2px",
                            },
                            "&.Mui-disabled": {
                                color: theme.palette.action.disabled,
                                borderColor: frameless ? "transparent" : theme.palette.divider,
                                bgcolor: frameless ? "transparent" : theme.palette.action.disabledBackground,
                            },
                            "@media (pointer: coarse)": {
                                minWidth: 44,
                                minHeight: 44,
                            },
                        },
                        ...(Array.isArray(sx) ? sx : [sx]),
                    ]}
                >
                    {icon}
                </IconButton>
            </span>
        </Tooltip>
    );
};
